import React, { useEffect, useState } from 'react';

import type { PolicyTocEntry } from '../../content/legal/slug';

interface PolicyDocumentTocProps {
  entries: PolicyTocEntry[];
}

const PolicyDocumentToc: React.FC<PolicyDocumentTocProps> = ({ entries }) => {
  const [activeId, setActiveId] = useState(entries[0]?.id ?? '');

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash) {
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveId(hash);
      });
    }
  }, [entries]);

  useEffect(() => {
    if (!entries.length) return;

    const observer = new IntersectionObserver(
      (observed) => {
        const visible = observed
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: 0.1 },
    );

    entries.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [entries]);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  if (!entries.length) return null;

  return (
    <>
      {/* Mobile: compact jump menu */}
      <nav className="policy-doc-toc policy-doc-toc--mobile" aria-label="On this page">
        <label htmlFor="policy-toc-select" className="policy-doc-toc__mobile-label">
          On this page
        </label>
        <select
          id="policy-toc-select"
          className="policy-doc-toc__mobile-select"
          value={activeId}
          onChange={(e) => handleClick(e.target.value)}
        >
          {entries.map(({ id, label, level }) => (
            <option key={id} value={id}>
              {level === 3 ? `  ${label}` : label}
            </option>
          ))}
        </select>
      </nav>

      {/* Desktop: sticky left index */}
      <nav className="policy-doc-toc policy-doc-toc--desktop" aria-label="Table of contents">
        <p className="policy-doc-toc__heading">On this page</p>
        <ul className="policy-doc-toc__list">
          {entries.map(({ id, label, level }) => (
            <li
              key={id}
              className={`policy-doc-toc__item policy-doc-toc__item--level-${level}${
                activeId === id ? ' policy-doc-toc__item--active' : ''
              }`}
            >
              <button type="button" className="policy-doc-toc__link" onClick={() => handleClick(id)}>
                {label.replace(/^\d+(\.\d+)*\.?\s*/, '')}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
};

export default PolicyDocumentToc;
